import { Empty } from '@creditornot/cb-placeholders';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { useThemeMode } from 'common/themeMode';
import { useMerchantContext } from 'modules/merchant/MerchantContext';

import { LoadingOverlay } from './LoadingOverlay';
import { useGetOfferByMerchantID } from './useGetOfferByMerchantID';

type AllowedLocale = 'en-GB' | 'de-DE' | 'da-DK' | 'sv-SV' | 'pl-PL' | 'fi-FI';

const Root = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
`;

const ErrorWrapper = styled.div`
  justify-content: center;
  display: flex;
  width: 100%;
  height: 100%;
`;

const languageMapping: Record<string, AllowedLocale> = {
  en: 'en-GB',
  de: 'de-DE',
  da: 'da-DK',
  sv: 'sv-SV',
  pl: 'pl-PL',
  fi: 'fi-FI',
};

const withFrameLanguage = (link: string, locale: AllowedLocale) => {
  const url = new URL(link);
  url.searchParams.append('lng', locale);
  return url.toString();
};

export const WoltCapitalOffer = ({ merchantId }: { merchantId: string }) => {
  const [isIframeLoaded, setIframeLoaded] = useState(false);
  const intl = useIntl();
  const { locale } = useIntl();
  const { themeMode } = useThemeMode();
  const { merchantsOffer, merchantsOfferError, isLoadingMerchantsOffer } = useGetOfferByMerchantID(merchantId);

  const activeLang = locale as Language;
  const activeLocale = languageMapping[activeLang] ?? 'en-GB';
  const signedLink = merchantsOffer?.url;

  useEffect(() => {
    setIframeLoaded(false);
  }, [activeLocale, themeMode]);

  if (isLoadingMerchantsOffer) {
    return (
      <Root>
        <LoadingOverlay />
      </Root>
    );
  }

  if (merchantsOfferError || !signedLink) {
    return (
      <ErrorWrapper>
        <Empty
          title={intl.formatMessage({ id: 'wolt-capital.dashboard.no-offers' })}
          description={intl.formatMessage({ id: 'wolt-capital.dashboard.no-offers-description' })}
        />
      </ErrorWrapper>
    );
  }

  return (
    <Root>
      {!isIframeLoaded && <LoadingOverlay />}
      <iframe
        key={`${activeLocale}-${themeMode}`}
        onLoad={() => setIframeLoaded(true)}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
        }}
        src={withFrameLanguage(signedLink, activeLocale)}
      />
    </Root>
  );
};

const WoltCapital = () => {
  const {
    merchant: {
      itemid: { $oid: merchantId },
    },
  } = useMerchantContext();
  return <WoltCapitalOffer merchantId={merchantId} />;
};

export default WoltCapital;
